/**
 * multi-variant-colour.js
 *
 * Renders ONLY the colour swatch widget. Sits upstream of the
 * design-placement block in the product page so customers pick a colour
 * before positioning their design. The per-size quantity table is
 * handled separately by <multi-variant-quantity>.
 *
 * Communication:
 *   - Dispatches 'mvq:colour-changed' on document whenever a colour is
 *     selected. Detail: { colour, variants, options, colourIdx }.
 *     <multi-variant-quantity> and design-placement-block listen for this.
 *   - Listens for 'mvq:request-colour' and re-broadcasts current colour
 *     (used by the quantity element on connect to bootstrap its state).
 *
 * Other elements can also read the current colour synchronously via
 *   document.querySelector('multi-variant-colour').selectedColour
 */

(function () {
  'use strict';

  // ── Gallery sync policy ────────────────────────────────────────────────────
  //
  // The gallery is only re-pointed at a colour's variant image when the colour
  // was actually CHOSEN — a swatch click, or a ?variant= in the URL. On a plain
  // arrival the product's own featured image (its first media) has to stay put:
  // collection cards link without ?variant=, so syncing there would silently
  // replace the hero shot with whichever image colours[0] happens to own, and
  // the customer would land on the second photo.
  //
  // Pure and unit-tested in tests/multi-variant-colour-gallery.test.mjs.
  function shouldSyncGallery(urlColour, userColour) {
    return Boolean(urlColour) || Boolean(userColour);
  }

  // Exported for the node unit tests; a no-op in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { shouldSyncGallery };
  }

  if (customElements.get('multi-variant-colour')) return;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function parseDataJSON(raw) {
    try {
      const ta = document.createElement('textarea');
      ta.innerHTML = raw || '';
      return JSON.parse(ta.value);
    } catch (e) { return null; }
  }

  function esc(text) {
    const d = document.createElement('div');
    d.textContent = String(text ?? '');
    return d.innerHTML;
  }

  // Variants come from two sources — Liquid (initial render) and the
  // /products/{handle}.js AJAX endpoint (top-up). Both nest the media as
  // `featured_media: { id, position, ... }`. The gallery sync code reads
  // FLAT `featured_media_id`, so we normalise once at parse time.
  function normaliseVariant(v) {
    if (!v) return v;
    if (v.featured_media_id != null) return v;          // already flat
    return {
      ...v,
      featured_media_id:       v.featured_media?.id ?? null,
      featured_media_position: v.featured_media?.position ?? null,
    };
  }

  // ── Custom Element ─────────────────────────────────────────────────────────

  class MultiVariantColour extends HTMLElement {

    constructor() {
      super();
      this.selectedColour = null;
      this.variants  = [];
      this.options   = [];
      this.colourMap = {};
      this.colourIdx = -1;
      this._userSelectedColour = null;
    }

    connectedCallback() {
      this.colourOptionName = this.dataset.colourOption || 'Colour';

      this.variants  = (parseDataJSON(this.dataset.variants) || []).map(normaliseVariant);
      this.options   = parseDataJSON(this.dataset.options)   || [];
      this.colourMap = parseDataJSON(this.dataset.colourMap) || {};

      const nameLower = n => (n || '').trim().toLowerCase();
      this.colourIdx = this.options.findIndex(o => {
        const lc = nameLower(o.name);
        return lc === nameLower(this.colourOptionName)
            || lc === 'colour'
            || lc === 'color';
      });

      if (this.colourIdx < 0) {
        console.warn(`[mvq-colour] Colour option not found. Looked for "${this.colourOptionName}". Available:`, this.options.map(o => o.name));
      }

      this.sectionEl   = this.querySelector('[data-colour-section]');
      this.swatchesEl  = this.querySelector('[data-swatches]');
      this.colourLabel = this.querySelector('[data-selected-colour-label]');

      const colours = this.uniqueColours();
      if (this.colourIdx >= 0 && colours.length > 1) {
        this.buildSwatches(colours);
      } else {
        // Single-colour or unrecognised option — hide UI but still broadcast
        // so downstream elements know what colour to filter by.
        if (this.sectionEl) this.sectionEl.style.display = 'none';
        this.selectedColour = colours[0] || null;
        this.broadcastChange();
      }

      this.topUpVariants();

      // Quantity element calls this on connect to bootstrap its state.
      document.addEventListener('mvq:request-colour', () => this.broadcastChange());

      // Remove injected gallery slides when the original Trade variant
      // picker fires (preserves the behaviour of the unsplit original).
      document.addEventListener('change', e => {
        if (e.target.matches('input[name^="Colour-"], input[name^="Size-"], select[id^="Option"]')) {
          document.querySelectorAll('[data-mvq-injected]').forEach(el => el.remove());
        }
      });
    }

    // ── Variant top-up (>100 variants) ───────────────────────────────────────
    //
    // Liquid caps product.variants at 100. For products with more variants we
    // fetch the full list and rebuild option values. We then re-broadcast,
    // and the quantity element will adopt the fuller variant set.

    async topUpVariants() {
      const handle = this.dataset.productHandle;
      let product;
      try {
        const res = await fetch(`/products/${handle}.js?variant_limit=1000`);
        if (!res.ok) return;
        product = await res.json();
      } catch (e) {
        console.error('[mvq-colour] variant top-up failed', e);
        return;
      }

      if (!product?.variants || product.variants.length <= this.variants.length) return;

      this.variants = product.variants.map(v => ({
        id:                      v.id,
        title:                   v.title,
        price:                   v.price,
        compare_at_price:        v.compare_at_price,
        available:               v.available,
        inventory_quantity:      v.inventory_quantity,
        inventory_management:    v.inventory_management,
        inventory_policy:        v.inventory_policy,
        options:                 v.options,
        featured_media_id:       v.featured_media?.id ?? null,
        featured_media_position: v.featured_media?.position ?? null,
      }));

      // Rebuild colour values from the full variant set.
      if (this.colourIdx >= 0 && this.options[this.colourIdx]) {
        const seen = new Set();
        const ordered = [];
        (this.options[this.colourIdx].values || []).forEach(val => {
          if (!seen.has(val)) { seen.add(val); ordered.push(val); }
        });
        this.variants.forEach(v => {
          const val = v.options?.[this.colourIdx];
          if (val && !seen.has(val)) { seen.add(val); ordered.push(val); }
        });
        this.options[this.colourIdx].values = ordered;
      }

      const colours = this.uniqueColours();
      if (this.colourIdx >= 0 && colours.length > 1) {
        if (this.sectionEl) this.sectionEl.style.display = '';
        
        const userPick = this._userSelectedColour;
        this.buildSwatches(colours);
        if (userPick && colours.includes(userPick) && userPick !== this.selectedColour) {
          this.selectColour(userPick, true);
        }
        
      } else {
        // Variants changed but UI doesn't — still re-broadcast so the
        // quantity element gets the fuller variant list.
        this.broadcastChange();
      }
    }

    // ── Variant helpers ──────────────────────────────────────────────────────

    uniqueColours() {
      if (this.colourIdx < 0) return [];
      return this.options[this.colourIdx]?.values || [];
    }

    variantsForColour(colour) {
      if (this.colourIdx < 0 || !colour) return this.variants;
      return this.variants.filter(v => v.options?.[this.colourIdx] === colour);
    }

    // ── Swatches ─────────────────────────────────────────────────────────────

    buildSwatches(colours) {
      this.swatchesEl.innerHTML = '';

      colours.forEach(colour => {
        const hex = this.colourMap[colour] || null;
        const btn = document.createElement('button');
        btn.type           = 'button';
        btn.dataset.colour = colour;
        btn.title          = colour;
        btn.setAttribute('aria-label', colour);
        btn.setAttribute('aria-pressed', 'false');

        if (hex) {
          btn.className = 'mvq__swatch mvq__swatch--color';
          btn.style.setProperty('--swatch-color', hex);
          btn.innerHTML = `<span class="mvq-sr-only">${esc(colour)}</span>`;
        } else {
          btn.className   = 'mvq__swatch mvq__swatch--text';
          btn.textContent = colour;
        }

        btn.addEventListener('click', () => {
          this._userSelectedColour = colour;
          this.selectColour(colour, true);
        });
        this.swatchesEl.appendChild(btn);
      });

      const urlColour  = this.colourFromUrlVariant();
      const userColour = this._userSelectedColour;
      this.selectColour(
        urlColour || userColour || colours[0],
        shouldSyncGallery(urlColour, userColour),
      );
    }

    selectColour(colour, syncGallery) {
      this.selectedColour = colour;

      this.swatchesEl?.querySelectorAll('[data-colour]').forEach(btn => {
        const active = btn.dataset.colour === colour;
        btn.classList.toggle('mvq__swatch--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (this.colourLabel) this.colourLabel.textContent = colour;

      this.broadcastChange();
      if (syncGallery) this.updateGalleryForColour(colour);
    }

    colourFromUrlVariant() {
      try {
        const variantId = parseInt(new URL(window.location.href).searchParams.get('variant'), 10);
        if (!variantId || this.colourIdx < 0) return null;
        const v = this.variants.find(v => v.id === variantId);
        return v?.options?.[this.colourIdx] || null;
      } catch (_) { return null; }
    }

    // ── Broadcast ────────────────────────────────────────────────────────────

    broadcastChange() {
      // Detail.colour is what design-placement-block listens for.
      // The extra fields let multi-variant-quantity adopt the fuller variant
      // list after top-up without doing its own fetch.
      document.dispatchEvent(new CustomEvent('mvq:colour-changed', {
        bubbles: true,
        detail:  {
          colour:    this.selectedColour,
          variants:  this.variants,
          options:   this.options,
          colourIdx: this.colourIdx,
        },
      }));
    }

    // ── Gallery sync (lifted verbatim from the original combined element) ───
    //
    // Updates the Dawn product media gallery to show the chosen colour's
    // featured image. Injects a slide if Trade hasn't pre-rendered one.

    updateGalleryForColour(colour) {
      const sectionId = this.dataset.sectionId;
      const gallery   = document.querySelector('media-gallery');
      if (!gallery) return;

      const sliderList = gallery.elements?.viewer?.querySelector('.product__media-list');
      if (!sliderList) return;

      // Reset any previously injected slides.
      sliderList.querySelectorAll('[data-mvq-injected]').forEach(el => el.remove());

      const variants       = this.variantsForColour(colour);
      const representative = variants.find(v => v.available) || variants[0];

      if (!representative?.featured_media_id) {
        const firstSlide = sliderList.querySelector('[data-media-id]');
        if (firstSlide) gallery.setActiveMedia(firstSlide.dataset.mediaId, false);
        return;
      }

      const mediaId = representative.featured_media_id;
      const fullKey = `${sectionId}-${mediaId}`;

      if (!gallery.elements.viewer.querySelector(`[data-media-id="${fullKey}"]`)) {
        const thumbItem = document.querySelector(`[data-target="${fullKey}"]`);
        const thumbImg  = thumbItem?.querySelector('img');

        if (!thumbImg) {
          const firstSlide = sliderList.querySelector('[data-media-id]');
          if (firstSlide) gallery.setActiveMedia(firstSlide.dataset.mediaId, false);
          return;
        }

        const srcEntries = (thumbImg.getAttribute('srcset') || '')
          .split(',')
          .map(e => { const p = e.trim().split(' '); return { url: p[0], w: parseInt(p[1]) || 0 }; })
          .sort((a, b) => b.w - a.w);

        const li = document.createElement('li');
        li.id                  = `Slide-${fullKey}`;
        li.className           = 'product__media-item grid__item slider__slide';
        li.dataset.mediaId     = fullKey;
        li.dataset.mvqInjected = '1';
        li.setAttribute('role', 'presentation');

        const container = document.createElement('div');
        container.className     = 'product-media-container media-type-image ' +
                                  'media-fit-cover global-media-settings gradient';
        container.style.cssText = '--ratio:1.0;--preview-ratio:1.0;';

        const mediaDiv     = document.createElement('div');
        mediaDiv.className = 'product__media media media--transparent';

        const img     = document.createElement('img');
        img.src       = srcEntries[0]?.url || thumbImg.src;
        img.alt       = thumbImg.alt || '';
        img.width     = 1946;
        img.height    = 1946;
        img.className = 'image-magnify-lightbox';
        const sizes   = thumbImg.getAttribute('sizes');
        if (sizes) img.setAttribute('sizes', sizes);
        img.srcset = srcEntries.filter(e => e.w <= 1946).map(e => `${e.url} ${e.w}w`).join(', ');

        mediaDiv.appendChild(img);
        container.appendChild(mediaDiv);
        li.appendChild(container);
        // Appended, not prepended: an injected colour slide must not displace
        // the product's front image from the head of the gallery. Only reached
        // when the colour's image isn't already a rendered slide, i.e. when
        // hide_variants keeps variant images out of the gallery.
        sliderList.appendChild(li);
      }

      gallery.setActiveMedia(fullKey, false);

      // Force thumbnail strip to scroll into view.
      const thumbEl = gallery.elements.thumbnails;
      if (thumbEl) {
        const thumbnail = thumbEl.querySelector(`[data-target="${fullKey}"]`);
        if (thumbnail) {
          const origIsSlideVisible = thumbEl.isSlideVisible.bind(thumbEl);
          thumbEl.isSlideVisible   = () => false;
          gallery.setActiveThumbnail(thumbnail);
          thumbEl.isSlideVisible   = origIsSlideVisible;
        }
      }
    }
  }

  customElements.define('multi-variant-colour', MultiVariantColour);
})();